import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import IntegrationActions from '../IntegrationActions';
import { importFromProcore, listFromProcore, sendToProcore } from '../procoreActions';
import { asList, getJson, integrationError, money, postJson, statusClass, integrationLabel, isProjectToolIntegration, isSyncableIntegration } from '../helpers';

function ProcoreMark() {
  return (
    <svg className="pc-procore-mark" viewBox="0 0 16 16" aria-label="Synced with Procore">
      <title>Synced with Procore</title>
      <polygon points="8,1 14.5,4.5 14.5,11.5 8,15 1.5,11.5 1.5,4.5" fill="hsl(19, 100%, 60%)" />
    </svg>
  );
}

function mappedVuids(mappings, integrationVuid, objectType) {
  const ids = new Set();
  mappings.forEach((mapping) => {
    if (mapping.integration_vuid !== integrationVuid) return;
    if (objectType && mapping.object_type !== objectType) return;
    if (mapping.object_vuid) ids.add(String(mapping.object_vuid));
  });
  return ids;
}

function mappedExternalIds(mappings, integrationVuid, objectType) {
  const ids = new Set();
  mappings.forEach((mapping) => {
    if (mapping.integration_vuid !== integrationVuid) return;
    if (objectType && mapping.object_type !== objectType) return;
    if (mapping.external_id) ids.add(String(mapping.external_id));
  });
  return ids;
}

const emptyProjectForm = {
  project_name: '',
  project_number: '',
  description: '',
  status: 'active',
  start_date: '',
  end_date: '',
};

export default function PortfolioPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);

  const reload = () => setRefreshKey((value) => value + 1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getJson('/api/projects')
      .then((data) => {
        if (!cancelled) setProjects(asList(data));
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Unable to load projects');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    Promise.all([
      getJson('/api/integrations'),
      getJson('/api/external-system-ids', { object_type: 'project' }),
    ])
      .then(([integrationRows, mappingRows]) => {
        if (cancelled) return;
        setIntegrations(
          asList(integrationRows).filter((row) => {
            if (String(row.status || '').toLowerCase() !== 'active') return false;
            return isSyncableIntegration(row);
          })
        );
        setMappings(asList(mappingRows));
      })
      .catch(() => {
        if (!cancelled) setMappings([]);
      });

    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const procoreVuids = useMemo(() => {
    const procoreIds = new Set(
      integrations.filter((row) => isProjectToolIntegration(row)).map((row) => row.vuid)
    );
    const ids = new Set();
    mappings.forEach((mapping) => {
      const isProcore = isProjectToolIntegration(mapping.integration) || procoreIds.has(mapping.integration_vuid);
      if (!isProcore || !mapping.object_vuid) return;
      ids.add(String(mapping.object_vuid));
    });
    return ids;
  }, [mappings, integrations]);

  const rows = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return projects;
    return projects.filter((project) => {
      return (
        String(project.project_name || '').toLowerCase().includes(term) ||
        String(project.project_number || '').toLowerCase().includes(term)
      );
    });
  }, [projects, query]);

  return (
    <div className="pc-page">
      <div className="pc-crumb">Company / Portfolio</div>
      <div className="pc-title-row">
        <h1>Portfolio</h1>
        <div className="pc-title-actions">
          <button type="button" className="pc-btn" onClick={() => setCreateOpen(true)}>
            Create project
          </button>
          <button type="button" className="pc-btn pc-btn-secondary" onClick={() => setSyncOpen(true)}>
            Sync
          </button>
        </div>
      </div>
      <div className="pc-toolbar">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Filter projects"
        />
      </div>
      <div className="pc-card">
        {loading ? (
          <div className="pc-empty">Loading projects…</div>
        ) : error ? (
          <div className="pc-empty">{error}</div>
        ) : rows.length === 0 ? (
          <div className="pc-empty">No projects match this filter.</div>
        ) : (
          <table className="pc-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Number</th>
                <th>Status</th>
                <th className="pc-right">Contract Value</th>
                <th className="pc-right">Current Budget</th>
                <th>Procore</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((project) => (
                <tr
                  key={project.vuid}
                  className="pc-clickable"
                  onClick={() => navigate(`/procore/projects/${project.vuid}`)}
                >
                  <td>{project.project_name}</td>
                  <td className="pc-muted">{project.project_number}</td>
                  <td>
                    <span className={`pc-pill ${statusClass(project.status)}`}>
                      {project.status || 'active'}
                    </span>
                  </td>
                  <td className="pc-right">{money(project.total_contract_value || project.contract_value)}</td>
                  <td className="pc-right">{money(project.current_budget || project.revised_budget)}</td>
                  <td className="pc-sync-cell">
                    {procoreVuids.has(String(project.vuid)) ? (
                      <ProcoreMark />
                    ) : (
                      <IntegrationActions
                        compact
                        sendKind="project"
                        sendPayload={{ project_vuid: project.vuid }}
                        onComplete={reload}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {createOpen ? (
        <CreateProjectModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            reload();
          }}
        />
      ) : null}
      {syncOpen ? (
        <PortfolioSyncModal
          integrations={integrations}
          mappings={mappings}
          projects={projects}
          onClose={() => setSyncOpen(false)}
          onComplete={() => {
            setSyncOpen(false);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateProjectModal({ onClose, onCreated }) {
  const [form, setForm] = useState(emptyProjectForm);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);

  const setField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.project_name.trim() || !form.project_number.trim()) {
      setFlash({ type: 'err', text: 'Project name and project number are required.' });
      return;
    }
    setBusy(true);
    setFlash(null);
    try {
      await postJson('/api/projects', {
        project_name: form.project_name.trim(),
        project_number: form.project_number.trim(),
        description: form.description.trim() || undefined,
        status: form.status || 'active',
        start_date: form.start_date || undefined,
        end_date: form.end_date || undefined,
      });
      onCreated();
    } catch (err) {
      setFlash({ type: 'err', text: integrationError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="pc-modal" onClick={(event) => event.stopPropagation()}>
        <div className="pc-modal-head">
          <h2>Create project</h2>
          <button type="button" className="pc-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <form onSubmit={submit}>
          <div className="pc-modal-body">
            <div className="pc-form-grid">
              <label className="pc-form-row">
                <span>Project name *</span>
                <input
                  value={form.project_name}
                  onChange={(event) => setField('project_name', event.target.value)}
                  placeholder="Enter project name"
                />
              </label>
              <label className="pc-form-row">
                <span>Project number *</span>
                <input
                  value={form.project_number}
                  onChange={(event) => setField('project_number', event.target.value)}
                  placeholder="Enter project number"
                />
              </label>
            </div>
            <label className="pc-form-row">
              <span>Description</span>
              <textarea
                value={form.description}
                onChange={(event) => setField('description', event.target.value)}
                placeholder="Enter project description"
                rows={3}
              />
            </label>
            <div className="pc-form-grid pc-form-grid-3">
              <label className="pc-form-row">
                <span>Start date</span>
                <input
                  type="date"
                  value={form.start_date}
                  onChange={(event) => setField('start_date', event.target.value)}
                />
              </label>
              <label className="pc-form-row">
                <span>End date</span>
                <input
                  type="date"
                  value={form.end_date}
                  onChange={(event) => setField('end_date', event.target.value)}
                />
              </label>
              <label className="pc-form-row">
                <span>Status</span>
                <select value={form.status} onChange={(event) => setField('status', event.target.value)}>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="on-hold">On Hold</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>
            </div>
            {flash ? <div className={`pc-flash ${flash.type === 'ok' ? 'pc-flash-ok' : 'pc-flash-err'}`}>{flash.text}</div> : null}
          </div>
          <div className="pc-modal-actions">
            <button type="button" className="pc-btn pc-btn-secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="pc-btn" disabled={busy}>
              {busy ? 'Creating…' : 'Create project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PortfolioSyncModal({ integrations, mappings, projects, onClose, onComplete }) {
  const [integrationVuid, setIntegrationVuid] = useState(integrations[0]?.vuid || '');
  const [direction, setDirection] = useState('');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState('');
  const [flash, setFlash] = useState(null);

  const integration = integrations.find((row) => row.vuid === integrationVuid) || integrations[0];
  const sendAllowed = isProjectToolIntegration(integration);
  const receiveAllowed = isProjectToolIntegration(integration);

  const resetChoice = () => {
    setDirection('');
    setItems([]);
    setSelected([]);
    setFlash(null);
  };

  useEffect(() => {
    resetChoice();
  }, [integrationVuid]);

  const loadItems = async (nextDirection) => {
    if (!integration) return;
    setBusy('load');
    setFlash(null);
    try {
      if (isProjectToolIntegration(integration) && nextDirection === 'send') {
        const synced = mappedVuids(mappings, integration.vuid, 'project');
        const next = projects
          .filter((project) => !synced.has(String(project.vuid)))
          .map((project) => ({
            id: project.vuid,
            title: project.project_name,
            subtitle: project.project_number || '',
            payload: { project_vuid: project.vuid },
          }));
        setItems(next);
        setSelected(next.map((row) => row.id));
      } else if (isProjectToolIntegration(integration) && nextDirection === 'receive') {
        const { items: remote, warnings } = await listFromProcore('projects', integration.vuid);
        const syncedExternal = mappedExternalIds(mappings, integration.vuid, 'project');
        const next = remote
          .filter((row) => !row.alreadyImported && !syncedExternal.has(String(row.id)))
          .map((row) => ({
            id: row.id,
            title: row.title,
            subtitle: row.subtitle || 'Project',
            raw: row,
          }));
        setItems(next);
        setSelected(next.map((row) => row.id));
        if (warnings?.length) setFlash({ type: 'err', text: warnings.slice(0, 4).join('\n') });
      } else {
        setItems([]);
        setSelected([]);
      }
    } catch (err) {
      setItems([]);
      setSelected([]);
      setFlash({ type: 'err', text: integrationError(err) });
    } finally {
      setBusy('');
    }
  };

  const pickDirection = (nextDirection) => {
    setDirection(nextDirection);
    loadItems(nextDirection);
  };

  const toggleItem = (id) => {
    setSelected((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  };

  const runSync = async () => {
    if (!integration || !direction) return;
    const chosen = items.filter((row) => selected.includes(row.id));
    if (!chosen.length) {
      setFlash({ type: 'err', text: 'Select at least one record.' });
      return;
    }
    setBusy('sync');
    setFlash(null);
    const errors = [];
    let sent = 0;
    try {
      if (isProjectToolIntegration(integration) && direction === 'send') {
        for (const row of chosen) {
          try {
            await sendToProcore(integration.vuid, 'project', row.payload);
            sent += 1;
          } catch (err) {
            errors.push(`${row.title}: ${integrationError(err)}`);
          }
        }
      } else if (isProjectToolIntegration(integration) && direction === 'receive') {
        const result = await importFromProcore(
          'projects',
          integration.vuid,
          undefined,
          chosen.map((row) => row.raw)
        );
        sent = chosen.length;
        (result.errors || []).forEach((item) => errors.push(item));
      }
      const extra = errors.length ? `\n${errors.slice(0, 6).join('\n')}` : '';
      if (!sent && errors.length) {
        setFlash({ type: 'err', text: errors.slice(0, 6).join('\n') });
        return;
      }
      if (errors.length) {
        setFlash({ type: 'err', text: `Synced ${sent} item${sent === 1 ? '' : 's'}.${extra}` });
        return;
      }
      onComplete();
    } catch (err) {
      setFlash({ type: 'err', text: integrationError(err) });
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="pc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="pc-modal" onClick={(event) => event.stopPropagation()}>
        <div className="pc-modal-head">
          <h2>Sync projects</h2>
          <button type="button" className="pc-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="pc-modal-body">
          <p className="pc-muted" style={{ marginTop: 0 }}>
            Choose an integration, then send Claret projects or receive projects that are not synced.
          </p>
          {integrations.length === 0 ? (
            <p className="pc-flash pc-flash-err">No active integrations found.</p>
          ) : (
            <>
              <div className="pc-sync-options">
                {integrations.map((row) => (
                  <button
                    key={row.vuid}
                    type="button"
                    className={`pc-choice${integrationVuid === row.vuid ? ' is-selected' : ''}`}
                    onClick={() => {
                      setIntegrationVuid(row.vuid);
                      resetChoice();
                    }}
                  >
                    <strong>{row.integration_name}</strong>
                    <span>{integrationLabel(row)}</span>
                  </button>
                ))}
              </div>
              {integration ? (
                <div className="pc-sync-options">
                  <button
                    type="button"
                    className={`pc-choice${direction === 'send' ? ' is-selected' : ''}`}
                    disabled={!sendAllowed}
                    onClick={() => pickDirection('send')}
                  >
                    <strong>Send</strong>
                    <span>
                      {sendAllowed
                        ? `Push unsynced Claret projects to ${integrationLabel(integration)}.`
                        : 'Send is only available for the project tool.'}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`pc-choice${direction === 'receive' ? ' is-selected' : ''}`}
                    disabled={!receiveAllowed}
                    onClick={() => pickDirection('receive')}
                  >
                    <strong>Receive</strong>
                    <span>
                      {receiveAllowed
                        ? 'Pull unsynced projects from Procore.'
                        : 'Receive is only available for the project tool.'}
                    </span>
                  </button>
                </div>
              ) : null}
            </>
          )}
          {busy === 'load' ? <div className="pc-empty">Loading unsynced records…</div> : null}
          {direction && busy !== 'load' ? (
            items.length === 0 ? (
              <div className="pc-empty">No unsynced records for this integration.</div>
            ) : (
              <>
                <div className="pc-import-toolbar">
                  <button
                    type="button"
                    className="pc-btn pc-btn-secondary pc-btn-sm"
                    onClick={() => setSelected(items.map((row) => row.id))}
                  >
                    Select all
                  </button>
                  <button type="button" className="pc-btn pc-btn-secondary pc-btn-sm" onClick={() => setSelected([])}>
                    Clear
                  </button>
                  <span className="pc-muted">{selected.length} selected</span>
                </div>
                <div className="pc-import-list">
                  {items.map((row) => (
                    <label key={row.id} className="pc-import-row">
                      <input
                        type="checkbox"
                        checked={selected.includes(row.id)}
                        onChange={() => toggleItem(row.id)}
                      />
                      <span>
                        <strong>{row.title}</strong>
                        <span className="pc-meta">{row.subtitle}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </>
            )
          ) : null}
          {flash ? (
            <div className={`pc-flash ${flash.type === 'ok' ? 'pc-flash-ok' : 'pc-flash-err'}`}>{flash.text}</div>
          ) : null}
        </div>
        <div className="pc-modal-actions">
          <button type="button" className="pc-btn pc-btn-secondary" onClick={onClose} disabled={Boolean(busy)}>
            Cancel
          </button>
          <button
            type="button"
            className="pc-btn"
            onClick={runSync}
            disabled={Boolean(busy) || !direction || !selected.length}
          >
            {busy === 'sync' ? 'Syncing…' : direction === 'receive' ? `Receive ${selected.length}` : `Send ${selected.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}
